import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { handleError } from '../utils/lib';
import { useAppBar } from '../providers/AppBarProvider';
import { useSnackbar } from '../providers/SnackBarProvider';
import CreditorsReport from '../components/reports/CreditorsReport';
import CreditorsToleranceReport from '../components/reports/CreditorsToleranceReport';
import AgendaReport from '../components/reports/AgendaReport';
import AgendaCommonReport from '../components/reports/AgendaCommonReport';
import LowFoodReport from '../components/reports/LowFoodReport';
import PurchasesReport from '../components/reports/PurchasesReport';
import DebtorsReport from '../components/reports/DebtorsReport';
import DebtorsToleranceReport from '../components/reports/DebtorsToleranceReport';
import SpendingsReport from '../components/reports/SpendingsReport';
import CashMovementsReport from '../components/reports/CashMovementsReport';
import SellsReport from '../components/reports/SellsReport';
import SellsByProductReport from '../components/reports/SellsByProductReport';
import SellsByOwnerReport from '../components/reports/SellsByOwnerReport';
import SellsByCategoryReport from '../components/reports/SellsByCategoryReport';
import MovementsReport from '../components/reports/MovementsReport';
import SupplyReport from '../components/reports/SupplyReport';
import SupplyCommonReport from '../components/reports/SupplyCommonReport';
import ProductsReport from '../components/reports/ProductsReport';
import ProductsBelowMinimumReport from '../components/reports/ProductsBelowMinimumReport';
import ProductsTraceabilityReport from '../components/reports/ProductsTraceabilityReport';
import BirthdayPetsReport from '../components/reports/BirthdayPetsReport';
import LifeExpectancyReport from '../components/reports/LifeExpectancyReport';
import LogsReport from '../components/reports/LogsReport';
import NewVetUsersReport from '../components/reports/NewVetUsersReport';
import UsersJoinRequestReport from '../components/reports/UsersJoinRequestReport';
import InTransitMovementsReport from '../components/reports/InTransitMovementsReport';
import OwnersNotificationsReport from '../components/reports/OwnersNotificationsReport';
import ClinicalHistoriesReport from '../components/reports/ClinicalHistoriesReport';
import { useConfig } from '../providers/ConfigProvider';
import useNavigationHelperStore from '../store/navigationHelperStore';
import { useTranslation } from 'react-i18next';

function MainReports() {
  const snackbar = useSnackbar();
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const {config, isLoadingConfig} = useConfig();
  const {updateTitle, toggleFab} = useAppBar();
  const { selectedReport: navigationSelectedReport, setSelectedReport: setNavigationSelectedReport, setNavigation } = useNavigationHelperStore();
  
  useEffect(() => {
    const loadReports = async () => {
      setIsLoading(true);
      try {
        const availableReports = getAvailableReports();
        setReports(availableReports);
        updateTitle('', t('main.reports'));
        toggleFab(true);

        // console.log("Reports selectedReport", navigationSelectedReport);

        // Restore selected report from navigation state
        if (navigationSelectedReport) {
          setSelectedReport(navigationSelectedReport);
        }
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };

    loadReports();
  }, [config, navigationSelectedReport]);

  const getAvailableReports = () => {
    const petPlural = config?.vet?.pet_naming_es_plural || 'Mascotas';
    const ownerPlural = config?.vet?.owner_naming_es_plural || 'Propietarios';
    const petPluralLower = petPlural.toLowerCase();

    // Define all possible reports
    const allReports = [
      {
        "type": "CREDITORS",
        "report_es": t('reports.creditors'),
      },
      {
        "type": "CREDITORS_TOLERANCE",
        "report_es": t('reports.creditorsTolerance'),
      },
      {
        "type": "AGENDA",
        "report_es": t('reports.agendaReport'),
      },
      {
        "type": "TOMORROW_AGENDA_VET",
        "report_es": t('reports.agendaTomorrow'),
        "info": t('reports.reportDesc.TOMORROW_AGENDA_VET')
      },
      {
        "type": "TOMORROW_AGENDA_USER",
        "report_es": t('reports.agendaTomorrow') + t('reports.userReportSuffix'),
        "info": t('reports.reportDesc.TOMORROW_AGENDA_USER')
      },
      {
        "type": "PENDING_AGENDA_VET",
        "report_es": t('reports.agendaPending'),
        "info": t('reports.reportDesc.PENDING_AGENDA_VET')
      },
      {
        "type": "PENDING_AGENDA_USER",
        "report_es": t('reports.agendaPending') + t('reports.userReportSuffix'),
        "info": t('reports.reportDesc.PENDING_AGENDA_USER')
      },
      {
        "type": "EXPIRED_AGENDA_ALL_VET",
        "report_es": t('reports.agendaExpired'),
        "info": t('reports.reportDesc.EXPIRED_AGENDA_ALL_VET')
      },
      {
        "type": "EXPIRED_AGENDA_USER",
        "report_es": t('reports.agendaExpired') + t('reports.userReportSuffix'),
        "info": t('reports.reportDesc.EXPIRED_AGENDA_USER')
      },
      {
        "type": "EXPIRED_AGENDA_VET",
        "report_es": t('reports.agendaExpiredYesterday'),
        "info": t('reports.reportDesc.EXPIRED_AGENDA_VET')
      },
      {
        "type": "LOW_FOOD",
        "report_es": t('reports.lowFood'),
        "info": t('reports.reportDesc.LOW_FOOD', { petPlural, petPluralLower })
      },
      {
        "type": "PURCHASES",
        "report_es": t('reports.purchases'),
      },
      {
        "type": "BIRTHDAY_PETS",
        "report_es": t('reports.birthdayPets'),
      },
      {
        "type": "DEBTORS",
        "report_es": t('reports.debtors'),
      },
      {
        "type": "DEBTORS_TOLERANCE",
        "report_es": t('reports.debtorsTolerance'),
      },
      {
        "type": "LIFE_EXPECTANCY",
        "report_es": t('reports.lifeExpectancy'),
        "info": t('reports.reportDesc.LIFE_EXPECTANCY', { petPlural })
      },
      {
        "type": "SPENDINGS",
        "report_es": t('reports.spendingsReport'),
      },
      {
        "type": "LOGS",
        "report_es": t('reports.logs'),
      },
      {
        "type": "CASH_MOVEMENTS",
        "report_es": t('reports.cashMovements'),
      },
      {
        "type": "OWNERS_NOTIFICATIONS",
        "report_es": t('reports.notifications'),
        "info": t('reports.reportDesc.OWNERS_NOTIFICATIONS', { ownerPlural })
      },
      {
        "type": "USER_ACCEPTED_JOIN_REQUEST",
        "report_es": t('reports.newUsers'),
        "info": t('reports.reportDesc.USER_ACCEPTED_JOIN_REQUEST')
      },
      {
        "type": "PRODUCTS",
        "report_es": t('reports.productsReport'),
      },
      {
        "type": "PRODUCTS_BELOW_MINIMUN",
        "report_es": t('reports.productsBelowMinimum'),
      },
      {
        "type": "PRODUCTS_TRACEABILITY",
        "report_es": t('reports.productsTraceability'),
      },
      {
        "type": "OWNERS_PETS_CLINICS",
        "report_es": t('reports.clinicalHistories'),
        "info": t('reports.reportDesc.OWNERS_PETS_CLINICS')
      },
      {
        "type": "MOVEMENTS",
        "report_es": t('reports.inventoryMovements'),
      },
      {
        "type": "IN_TRANSIT_MOVEMENTS",
        "report_es": t('reports.inTransitMovements'),
      },
      // {
      //   "type": "WAITING_ROOM",
      //   "report_es": "Sala de espera",
      //   "info": "Pacientes esperando atención"
      // },
      // {
      //   "type": "WAITING_ROOM_AUTO_DELETED",
      //   "report_es": "Sala de espera (auto eliminados)",
      //   "info": "Pacientes eliminados automáticamente de sala de espera"
      // },
      {
        "type": "USER_JOIN_REQUEST",
        "report_es": t('reports.usersJoinRequests'),
        "info": t('reports.reportDesc.USER_JOIN_REQUEST')
      },
      {
        "type": "SUPPLY",
        "report_es": t('reports.supplyReport'),
      },
      {
        "type": "TOMORROW_SUPPLY_VET",
        "report_es": t('reports.supplyTomorrow'),
        "info": t('reports.reportDesc.TOMORROW_SUPPLY_VET')
      },
      {
        "type": "TOMORROW_SUPPLY_USER",
        "report_es": t('reports.supplyTomorrow') + t('reports.userReportSuffix'),
        "info": t('reports.reportDesc.TOMORROW_SUPPLY_USER')
      },
      {
        "type": "DOMICILIARY_PENDING_SUPPLY_VET",
        "report_es": t('reports.supplyDomiciliaryPending'),
        "info": t('reports.reportDesc.DOMICILIARY_PENDING_SUPPLY_VET')
      },
      {
        "type": "DOMICILIARY_EXPIRED_SUPPLY_VET",
        "report_es": t('reports.supplyDomiciliaryExpiredYesterday'),
        "info": t('reports.reportDesc.DOMICILIARY_EXPIRED_SUPPLY_VET')
      },
      {
        "type": "PENDING_SUPPLY_VET",
        "report_es": t('reports.supplyPending'),
        "info": t('reports.reportDesc.PENDING_SUPPLY_VET')
      },
      {
        "type": "PENDING_SUPPLY_USER",
        "report_es": t('reports.supplyPending') + t('reports.userReportSuffix'),
        "info": t('reports.reportDesc.PENDING_SUPPLY_USER')
      },
      {
        "type": "EXPIRED_SUPPLY_ALL_VET",
        "report_es": t('reports.supplyExpired'),
        "info": t('reports.reportDesc.EXPIRED_SUPPLY_ALL_VET')
      },
      {
        "type": "EXPIRED_SUPPLY_USER",
        "report_es": t('reports.supplyExpired') + t('reports.userReportSuffix'),
        "info": t('reports.reportDesc.EXPIRED_SUPPLY_USER')
      },
      {
        "type": "EXPIRED_SUPPLY_VET",
        "report_es": t('reports.supplyExpiredYesterday'),
        "info": t('reports.reportDesc.EXPIRED_SUPPLY_VET')
      },
      {
        "type": "SELLS",
        "report_es": t('reports.sells'),
      },
      {
        "type": "SELLS_BY_PRODUCT",
        "report_es": t('reports.sellsByProduct'),
      },
      {
        "type": "SELLS_BY_OWNER",
        "report_es": t('reports.sellsByOwner'),
      },
      {
        "type": "SELLS_BY_CATEGORY",
        "report_es": t('reports.sellsByCategory'),
      },
    ];
    
    // Filter reports based on user permissions
    return filterReportsByPermission(allReports);
  };

  const filterReportsByPermission = (availableReports) => {
    if (!config || !config.permissions || !config.permissions.reports_permissions) {
      return [];
    }

    const { reports_permissions } = config.permissions;
    
    // Filter reports that user has permission to access
    return availableReports.filter(report => {
      // Get the report type
      const reportType = report.type;
      
      // Convert to lowercase for case-insensitive comparison
      const reportTypeLower = reportType.toLowerCase();
      
      // Check direct permission (exact match case-insensitive)
      if (reports_permissions.hasOwnProperty(reportTypeLower) && reports_permissions[reportTypeLower] === 1) {
        return true;
      }
      
      // Special case: If user has PRODUCTS permission, also allow PRODUCTS_BELOW_MINIMUN and PRODUCTS_TRACEABILITY
      if ((reportType === 'PRODUCTS_BELOW_MINIMUN' /*|| reportType === 'PRODUCTS_TRACEABILITY'*/) && 
          reports_permissions.hasOwnProperty('products') && reports_permissions['products'] === 1) {
        return true;
      }

      // Special case: If user has SELLS permission, also allow SELLS_BY_* reports
      if ((reportType === 'SELLS_BY_PRODUCT' || reportType === 'SELLS_BY_OWNER' || reportType === 'SELLS_BY_CATEGORY') && 
          reports_permissions.hasOwnProperty('sells') && reports_permissions['sells'] === 1) {
        return true;
      }

      // Special case: database dump (owners, pets, clinical histories) for owners
      if (reportType === 'OWNERS_PETS_CLINICS' && config.rol === "OWNER") {
        return true;
      }
      
      // Return false if no matching permission is found
      return false;
    });
  };
  
  const handleReportChange = (event) => {
    const reportType = event.target.value;
    setSelectedReport(reportType);
    
    // Save selected report in navigation store
    if (reportType) {
      setNavigation('/main/reports', {});
      setNavigationSelectedReport(reportType);
    }
  };

  const renderReport = (reportType) => {
    if (!reportType) return null;
    
    switch (reportType) {
      // Acreedores
      case 'CREDITORS':
        return <CreditorsReport />;
      case 'CREDITORS_TOLERANCE':
        return <CreditorsToleranceReport />;
      
      // Agenda
      case 'AGENDA':
        return <AgendaReport />;
      case 'TOMORROW_AGENDA_VET':
        return <AgendaCommonReport reportType="TOMORROW_AGENDA_VET" title={t('reports.agendaTomorrow')} />;
      case 'TOMORROW_AGENDA_USER':
        return <AgendaCommonReport reportType="TOMORROW_AGENDA_USER" title={t('reports.agendaTomorrow') + t('reports.userReportSuffix')} />;
      case 'PENDING_AGENDA_VET':
        return <AgendaCommonReport reportType="PENDING_AGENDA_VET" title={t('reports.agendaPending')} />;
      case 'PENDING_AGENDA_USER':
        return <AgendaCommonReport reportType="PENDING_AGENDA_USER" title={t('reports.agendaPending') + t('reports.userReportSuffix')} />;
      case 'EXPIRED_AGENDA_ALL_VET':
        return <AgendaCommonReport reportType="EXPIRED_AGENDA_ALL_VET" title={t('reports.agendaExpired')} />;
      case 'EXPIRED_AGENDA_USER':
        return <AgendaCommonReport reportType="EXPIRED_AGENDA_USER" title={t('reports.agendaExpired') + t('reports.userReportSuffix')} />;
      case 'EXPIRED_AGENDA_VET':
        return <AgendaCommonReport reportType="EXPIRED_AGENDA_VET" title={t('reports.agendaExpiredYesterday')} />;
      
      // Alimentación
      case 'LOW_FOOD':
        return <LowFoodReport />;
      
      // Finanzas
      case 'PURCHASES':
        return <PurchasesReport />;
      case 'DEBTORS':
        return <DebtorsReport />;
      case 'DEBTORS_TOLERANCE':
        return <DebtorsToleranceReport />;
      case 'SPENDINGS':
        return <SpendingsReport />;
      case 'CASH_MOVEMENTS':
        return <CashMovementsReport />;
      case 'SELLS':
        return <SellsReport />;
      case 'SELLS_BY_PRODUCT':
        return <SellsByProductReport />;
      case 'SELLS_BY_OWNER':
        return <SellsByOwnerReport />;
      case 'SELLS_BY_CATEGORY':
        return <SellsByCategoryReport />;
      case 'OWNERS_PETS_CLINICS':
        return <ClinicalHistoriesReport />;
      
      // Mascotas
      case 'BIRTHDAY_PETS':
        return <BirthdayPetsReport />;
      case 'LIFE_EXPECTANCY':
        return <LifeExpectancyReport />;
      
      // Sistema
      case 'LOGS':
        return <LogsReport />;
      case 'USER_ACCEPTED_JOIN_REQUEST':
        return <NewVetUsersReport />;
      case 'USER_JOIN_REQUEST':
        return <UsersJoinRequestReport />;
      case 'OWNERS_NOTIFICATIONS':
        return <OwnersNotificationsReport />;
      
      // Inventario
      case 'IN_TRANSIT_MOVEMENTS':
        return <InTransitMovementsReport />;
      case 'PRODUCTS':
        return <ProductsReport />;
      case 'PRODUCTS_BELOW_MINIMUN':
        return <ProductsBelowMinimumReport />;
      case 'PRODUCTS_TRACEABILITY':
        return <ProductsTraceabilityReport />;
      case 'MOVEMENTS':
        return <MovementsReport />;
      
      // Sala de espera
      case 'WAITING_ROOM':
        return <Typography>{t('reports.waitingRoomTitle')}</Typography>;
      case 'WAITING_ROOM_AUTO_DELETED':
        return <Typography>{t('reports.waitingRoomAutoDeleted')}</Typography>;
      
      // Suministros
      case 'SUPPLY':
        return <SupplyReport />;
      case 'TOMORROW_SUPPLY_VET':
        return <SupplyCommonReport reportType="TOMORROW_SUPPLY_VET" title={t('reports.supplyTomorrow')} />;
      case 'TOMORROW_SUPPLY_USER':
        return <SupplyCommonReport reportType="TOMORROW_SUPPLY_USER" title={t('reports.supplyTomorrow') + t('reports.userReportSuffix')} />;
      case 'DOMICILIARY_PENDING_SUPPLY_VET':
        return <SupplyCommonReport reportType="DOMICILIARY_PENDING_SUPPLY_VET" title={t('reports.supplyDomiciliaryPending')} />;
      case 'DOMICILIARY_EXPIRED_SUPPLY_VET':
        return <SupplyCommonReport reportType="DOMICILIARY_EXPIRED_SUPPLY_VET" title={t('reports.supplyDomiciliaryExpiredYesterday')} />;
      case 'PENDING_SUPPLY_VET':
        return <SupplyCommonReport reportType="PENDING_SUPPLY_VET" title={t('reports.supplyPending')} />;
      case 'PENDING_SUPPLY_USER':
        return <SupplyCommonReport reportType="PENDING_SUPPLY_USER" title={t('reports.supplyPending') + t('reports.userReportSuffix')} />;
      case 'EXPIRED_SUPPLY_ALL_VET':
        return <SupplyCommonReport reportType="EXPIRED_SUPPLY_ALL_VET" title={t('reports.supplyExpired')} />;
      case 'EXPIRED_SUPPLY_USER':
        return <SupplyCommonReport reportType="EXPIRED_SUPPLY_USER" title={t('reports.supplyExpired') + t('reports.userReportSuffix')} />;
      case 'EXPIRED_SUPPLY_VET':
        return <SupplyCommonReport reportType="EXPIRED_SUPPLY_VET" title={t('reports.supplyExpiredYesterday')} />;
      
      // No report found
      default:
        return <Typography>{t('reports.reportNotFound')}</Typography>;
    }
  };

  if (isLoading || isLoadingConfig) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const selectedReportMeta = selectedReport
    ? reports.find((r) => r.type === selectedReport)
    : null;

  return (
    <Container maxWidth="xl">
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth sx={{ mb: selectedReportMeta?.info ? 1 : 3 }}>
          <InputLabel>{t('reports.selectReport')}</InputLabel>
          <Select
            value={selectedReport}
            onChange={handleReportChange}
            label={t('reports.selectReport')}
            disabled={isLoading}
          >
            {reports.map(report => (
              <MenuItem key={report.type} value={report.type}>
                {report.report_es}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedReportMeta?.info ? (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1, mb: 2 }}>
            {selectedReportMeta.info}
          </Typography>
        ) : null}

        {selectedReport ? (
          renderReport(selectedReport)
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <Typography variant="body1" color="text.secondary">
              {t('reports.selectReportPlaceholder')}
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default MainReports;
